export type TestAccuracySection = 'verification' | 'execution';
/** API query param; UI sends verification | execution only (never all). */
export type TestAccuracySectionParam = TestAccuracySection | 'all';
export type TestAccuracyPeriod = '7' | '30' | 'custom';

export type VerificationBucket =
  | 'passed_1st_attempt'
  | 'passed_2nd_attempt'
  | 'passed_3rd_plus_attempt'
  | 'not_passed';

export type ExecutionBucket =
  | 'passed_after_first_verify'
  | 'required_reverification'
  | 'never_passed_after_verify'
  | 'regressed';

export interface ITestAccuracyOrg {
  org_id: string;
  org_name: string;
}

export interface ITestAccuracyProject {
  project_id: string;
  project_name: string;
  code?: string;
}

export interface ITestAccuracyOrgsResponse {
  orgs: ITestAccuracyOrg[];
}

export interface ITestAccuracyProjectsResponse {
  projects: ITestAccuracyProject[];
}

export interface ITestCaseResult {
  identifier?: string;
  name?: string;
  bucket?: string;
  run_by?: string;
}

export interface IVerificationMetrics {
  total_executed?: number;
  passed_1st_attempt?: number;
  passed_2nd_attempt?: number;
  passed_3rd_plus_attempt?: number;
  not_passed?: number;
  /** Preferred pass % from backend when present. */
  efficiency_pct?: number | null;
  pass_rate?: number | null;
  run_total?: number;
  run_passed?: number;
  run_failed?: number;
  run_pass_rate?: number | null;
  test_cases?: ITestCaseResult[];
}

export interface IExecutionMetrics {
  total_executed?: number;
  passed_after_first_verify?: number;
  required_reverification?: number;
  never_passed_after_verify?: number;
  regressed?: number;
  run_total?: number;
  run_passed?: number;
  run_failed?: number;
  run_pass_rate?: number | null;
  test_cases?: ITestCaseResult[];
}

export interface ITestAccuracyDay {
  date: string;
  verification?: IVerificationMetrics;
  execution?: IExecutionMetrics;
}

export interface ITestAccuracySummary {
  verification?: IVerificationMetrics;
  execution?: IExecutionMetrics;
}

export interface ITestAccuracyDailyParams {
  org_id: string;
  project_id?: string;
  test_type?: string;
  /** Backend: all | nlp | gen_ai | ui_nlp (aliases: gene, genai, gen-ai, ui-nlp) */
  source?: string;
  from_date?: string;
  to_date?: string;
  /** verification | execution (required for dashboard); omit or all = legacy both sections */
  section?: TestAccuracySection;
}

export interface ITestAccuracyStreamMeta {
  type: 'meta';
  org_id?: string;
  org_name?: string;
  section?: TestAccuracySection;
  period?: { from?: string; to?: string; from_date?: string; to_date?: string };
  filters?: Record<string, unknown>;
  day_count?: number;
}

export interface ITestAccuracyStreamSummary {
  type: 'summary';
  summary: ITestAccuracySummary;
}

export interface ITestAccuracyStreamDay {
  type: 'day';
  day: ITestAccuracyDay;
}

export interface ITestAccuracyStreamError {
  type: 'error';
  message?: string;
}

export interface ITestAccuracyStreamDone {
  type: 'done';
}

export type TestAccuracyStreamEvent =
  | ITestAccuracyStreamMeta
  | ITestAccuracyStreamSummary
  | ITestAccuracyStreamDay
  | ITestAccuracyStreamError
  | ITestAccuracyStreamDone
  | { type: string; [key: string]: unknown };

export interface ITestAccuracyStreamHandlers {
  meta?: (evt: ITestAccuracyStreamMeta) => void;
  summary?: (evt: ITestAccuracyStreamSummary) => void;
  day?: (evt: ITestAccuracyStreamDay) => void;
  done?: (evt: ITestAccuracyStreamDone) => void;
  error?: (evt: ITestAccuracyStreamError) => void;
}

export const TEST_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'web', label: 'Web' },
  { value: 'api', label: 'API' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'android_mobile', label: 'Mobile (Android)' },
  { value: 'ios_mobile', label: 'Mobile (iOS)' },
  { value: 'android_browser', label: 'Browser (Android)' },
  { value: 'ios_browser', label: 'Browser (iOS)' },
  { value: 'manual', label: 'Manual' },
  { value: 'agent', label: 'Agent' },
] as const;

/** Values match backend source filter (aliases accepted server-side). */
export const SOURCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'nlp', label: 'NLP' },
  { value: 'gen_ai', label: 'Gen AI' },
  { value: 'ui_nlp', label: 'UI NLP' },
] as const;
